import { useEffect, useState } from "react";
import { HealthMatchIcon } from "../icons/HealthMatchIcon";
import { LandingPageConfig } from "../types/landingPage";
import { LandingPageLayout } from "../components/LandingPageLayout";
import { usePartnerConfig, mergePartnerConfig } from "../hooks/usePartnerConfig";
import { track, getAnonymousId } from "../utils/rudderstack";
import { resolveUrlTemplate, getStoredAttribution, getRedirectToken } from "../utils/urlTemplate";
import { safeSessionStorage } from "../utils/safeStorage";
import { CheckCircle } from "lucide-react";

type QualifiedRedirectState = {
  countdown: number;
  hasRedirected: boolean;
};

const COUNTDOWN_SECONDS = 7;

export function QualifiedRedirectPageContent({ config }: { config: LandingPageConfig }) {
  const partnerConfig = usePartnerConfig();
  const [state, setState] = useState<QualifiedRedirectState>({
    countdown: COUNTDOWN_SECONDS,
    hasRedirected: false,
  });

  // Get RudderStack anonymous ID for attribution tracking
  // If RudderStack isn't initialized (dev mode), generate a session ID as fallback
  const getSessionId = () => {
    const rudderAnonymousId = getAnonymousId();
    if (rudderAnonymousId) {
      return rudderAnonymousId;
    }

    // Fallback: Generate or retrieve session ID from sessionStorage
    const sessionKey = `hm_session_${config.id}`;
    let sessionId = safeSessionStorage.getItem(sessionKey);
    if (!sessionId) {
      sessionId = `hm_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
      safeSessionStorage.setItem(sessionKey, sessionId);
    }
    return sessionId;
  };

  const anonymousId = getSessionId();
  const attribution = getStoredAttribution();
  const redirectToken = getRedirectToken(config.id);

  // Build redirect URL by replacing template variables
  // Supports: $anonymousId, $redirectToken, $source, $landingPageId, etc.
  const redirectUrl = config.externalRedirectUrl
    ? resolveUrlTemplate(config.externalRedirectUrl, {
        anonymousId,
        redirectToken,
        source: attribution.source,
        medium: attribution.medium,
        campaign: attribution.campaign,
        clickId: attribution.clickId,
        landingPageId: config.id,
      })
    : "https://app.healthmatch.io";

  const message = config.qualifiedRedirectMessage || "Great news! You may be suitable for this study.";

  useEffect(() => {
    // Track qualified redirect page view with anonymous ID for attribution
    track("lp.qualified_redirect.viewed", {
      landing_page_id: config.id,
      external_redirect_url: config.externalRedirectUrl,
      countdown_seconds: COUNTDOWN_SECONDS,
      anonymous_id: anonymousId,
      redirect_url_with_tracking: redirectUrl,
    });

    // Dedicated linkage event for joining inbound affiliate clicks with outbound redirects in Redshift
    track("lp.redirect_attribution_linkage", {
      landing_page_id: config.id,
      // Inbound (affiliate side)
      click_id: attribution.clickId,
      inbound_source: attribution.source,
      inbound_campaign: attribution.campaign,
      inbound_medium: attribution.medium,
      match_type: attribution.matchType,
      ad_set_id: attribution.adSetId,
      ad_id: attribution.adId,
      // Outbound (trialandyou side)
      anonymous_id: anonymousId,
      redirect_token: redirectToken,
      outbound_url: redirectUrl,
      outbound_utm_campaign: `hm_lp_${redirectToken}`,
      // Metadata
      redirect_type: "qualified_redirect",
      timestamp: new Date().toISOString(),
    });
  }, [config.id, config.externalRedirectUrl, anonymousId, redirectUrl, attribution]);

  // Handle countdown and redirect
  useEffect(() => {
    const { hasRedirected } = state;

    // Don't redirect if already redirected
    if (hasRedirected) {
      return;
    }

    const countdownInterval = setInterval(() => {
      setState((prev) => {
        if (prev.countdown <= 1 && !prev.hasRedirected) {
          clearInterval(countdownInterval);

          // Track countdown completion
          track("lp.qualified_redirect.countdown_completed", {
            landing_page_id: config.id,
            destination: redirectUrl,
            anonymous_id: anonymousId,
          });

          // CRITICAL: Track redirect event before the actual redirect happens
          // This event serves as backup attribution if Trial & You doesn't capture UTM params
          track("lp.qualified_redirect.redirected", {
            landing_page_id: config.id,
            destination_url: redirectUrl,
            destination_url_parsed: new URL(redirectUrl),
            redirect_token: redirectToken,
            utm_campaign_value: redirectToken,
            utm_campaign_full: `hm_lp_${redirectToken}`,
            trigger: "countdown",
            anonymous_id: anonymousId,
            timestamp: new Date().toISOString(),
          });

          // Small delay to ensure tracking completes before redirect
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 100);
          return { ...prev, countdown: 0, hasRedirected: true };
        }

        return { ...prev, countdown: prev.countdown - 1 };
      });
    }, 1000);

    return () => clearInterval(countdownInterval);
  }, [state, config.id, redirectUrl, anonymousId]);

  const handleManualRedirect = () => {
    // Track manual click
    track("lp.qualified_redirect.manual_click", {
      landing_page_id: config.id,
      destination: redirectUrl,
      countdown_remaining: state.countdown,
      anonymous_id: anonymousId,
    });

    // CRITICAL: Track redirect event before the actual redirect happens
    // This event serves as backup attribution if Trial & You doesn't capture UTM params
    track("lp.qualified_redirect.redirected", {
      landing_page_id: config.id,
      destination_url: redirectUrl,
      destination_url_parsed: new URL(redirectUrl),
      utm_campaign_value: anonymousId,
      utm_campaign_full: `hm_lp_${anonymousId}`,
      trigger: "manual_click",
      anonymous_id: anonymousId,
      timestamp: new Date().toISOString(),
    });

    // Small delay to ensure tracking completes before redirect
    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 100);
  };

  return (
    <LandingPageLayout
      title={config.title}
      description={config.description}
      {...mergePartnerConfig(partnerConfig, config)}
    >
      <div className="relative bg-white" data-testid="qualified-redirect-page">
        <div className="absolute inset-0 overflow-hidden pointer-events-none hidden md:block">
          <div className="absolute top-32 right-0 w-[200px] h-[200px] md:w-[350px] md:h-[350px] text-primary-500 floating-orb">
            <HealthMatchIcon />
          </div>

          <div className="absolute top-48 -left-16 w-[180px] h-[180px] md:w-[250px] md:h-[250px] text-primary-500 floating-orb-reverse">
            <HealthMatchIcon />
          </div>

          <div className="absolute bottom-48 right-8 w-[120px] h-[120px] md:right-32 md:w-[180px] md:h-[180px] text-primary-500 floating-orb-pulse">
            <HealthMatchIcon />
          </div>
        </div>

        <div
          className="relative z-10 isolation max-w-7xl mx-auto px-2 sm:px-3 lg:px-4 pt-4 md:pt-8 pb-4 md:pb-20"
          data-testid="qualified-redirect-content"
        >
          <div className="text-center">
            <div
              className="text-[1.625rem] leading-[1.3] md:leading-[1.2] tracking-tight font-bold text-gray-900 sm:text-3xl md:text-4xl px-2 sm:px-4"
              data-testid="qualified-redirect-title"
            >
              <span className="block mb-3 md:mb-4">
                You May Qualify!
              </span>
              <span className="block text-xl md:text-2xl bg-clip-text text-transparent bg-gradient-to-r from-primary-500 to-primary-600 leading-[1.4] md:leading-[1.3] overflow-visible">
                Let's continue to the trial website
              </span>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:gap-8">
            <div className="w-full max-w-4xl mx-auto px-2">
              <div
                className="bg-white rounded-2xl overflow-hidden p-8 flex flex-col items-center justify-center text-center shadow-lg border border-gray-100"
                data-testid="qualified-redirect-card"
              >
                <div className="mb-6 text-green-500">
                  <CheckCircle className="h-20 w-20 mx-auto" strokeWidth={1.5} />
                </div>

                <h3 className="text-2xl font-semibold text-gray-900 mb-4">
                  {message}
                </h3>

                <p className="text-lg text-gray-700 mb-6 max-w-2xl">
                  We're directing you to the trial website to complete the next step of pre-screening and to apply.
                </p>

                <p
                  className="text-sm text-gray-500 mb-8"
                  data-testid="qualified-redirect-countdown"
                >
                  Redirecting in {state.countdown}{" "}
                  {state.countdown === 1 ? "second" : "seconds"}...
                </p>

                <div className="flex flex-col sm:flex-row gap-4 w-full max-w-md">
                  <button
                    onClick={handleManualRedirect}
                    className="flex-1 inline-flex items-center justify-center px-6 py-3 border border-transparent rounded-lg text-base font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors"
                    data-testid="qualified-redirect-manual-button"
                  >
                    Continue Now
                  </button>
                </div>

                <p className="text-xs text-gray-500 mt-6">
                  By continuing, you'll be redirected to a secure partner website.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </LandingPageLayout>
  );
}
